// C10 — general notes as named, dated entries (P-g). Pure helpers shared by
// the worker (routes, feedback webhook, round-summary prompt) and the client.

export const MAX_NOTE_BODY_CHARS = 4000;
export const MAX_NOTE_AUTHOR_CHARS = 80;
export const MAX_NOTES_PER_SCRIPT = 100;
/** Title of the single entry migrated from the old shared notes blob. */
export const LEGACY_NOTES_TITLE = 'Earlier notes';

/** Single-line names for storage and readers of older, unsanitised entries. */
export function normalizeNoteAuthorName(name: string): string {
  return name.replace(/\s+/g, ' ').replace(/[\p{Cc}\p{Cf}]/gu, '').replace(/\s+/g, ' ').trim();
}

export interface NoteEntry {
  id: string;
  author_name: string;
  body: string;
  created_at: number;
  updated_at: number;
  /** The migrated "Earlier notes" blob: no date, never client-editable. */
  legacy: boolean;
  /** Changed after it was first added. */
  edited: boolean;
}

/** Client payload: `mine` is true when this browser's device token wrote it. */
export interface PublicNoteEntry extends NoteEntry {
  mine: boolean;
}

export function rowToNoteEntry(row: {
  id: string;
  author_name: string;
  body: string;
  created_at: number;
  updated_at: number;
  legacy: number;
}): NoteEntry {
  const legacy = row.legacy === 1;
  return {
    id: row.id,
    author_name: row.author_name,
    body: row.body,
    created_at: row.created_at,
    updated_at: row.updated_at,
    legacy,
    edited: !legacy && row.updated_at > row.created_at,
  };
}

/** Newest first, with "Earlier notes" always last. */
export function sortNoteEntries<T extends { legacy: boolean; created_at: number; id: string }>(
  entries: T[],
): T[] {
  return [...entries].sort((a, b) => {
    if (a.legacy !== b.legacy) return a.legacy ? 1 : -1;
    if (a.created_at !== b.created_at) return b.created_at - a.created_at;
    return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
  });
}

function cut(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max).trimEnd()}...` : text;
}

/**
 * Plain-text rendering for the feedback webhook (`client_notes_text`) and
 * the round-summary prompt, as mock-up 07 §03d:
 *
 *   Sarah Chen (4 Nov): Can we keep the runtime under two minutes?
 *
 *   Earlier notes: Margaret prefers "Mags" in conversation...
 */
export function formatClientNotesText(
  entries: NoteEntry[],
  opts: { maxBodyChars?: number; maxTotalChars?: number; timeZone?: string } = {},
): string {
  const maxBody = opts.maxBodyChars ?? 500;
  const maxTotal = opts.maxTotalChars ?? 4000;
  const date = new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    timeZone: opts.timeZone ?? 'Australia/Melbourne',
  });
  const text = sortNoteEntries(entries)
    .map((entry) => {
      const body = cut(entry.body.trim(), maxBody);
      if (entry.legacy) return `${LEGACY_NOTES_TITLE}: ${body}`;
      return `${normalizeNoteAuthorName(entry.author_name)} (${date.format(entry.created_at)}): ${body}`;
    })
    .join('\n\n');
  return cut(text, maxTotal);
}
